import React, { useRef, useEffect, useMemo } from "react";
import * as d3 from "d3";
import classNames from "classnames";

import { useAppSelector } from "src/redux/hooks";
import { CGM_PIE_CHART_DATA } from "src/constants/cgm";

export interface PieChartDataItem {
  name: string;
  value: number;
  color: string;
  range: string;
  ideal: string;
}

interface PieChartRepresentationProps {
  data?: PieChartDataItem[];
  wrapperClass?: string;
  legendWrapperClass?: string;
  width?: number;
  height?: number;
  showLegend?: boolean;
}

const PieChartRepresentation: React.FC<PieChartRepresentationProps> = ({
  data: dataProp,
  wrapperClass,
  legendWrapperClass,
  width = 300,
  height = 300,
  showLegend = true,
}) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const cgmCumulativeLogs = useAppSelector(
    (state) => state.cgm.cgmCumulativeLogs
  );

  const data = useMemo(() => {
    if (dataProp && dataProp.length > 0) return dataProp;
    const cumulativeData = cgmCumulativeLogs.data as Record<
      string,
      number
    > | null;
    return CGM_PIE_CHART_DATA.map((item) => {
      const raw = cumulativeData?.[item.key];
      const n = typeof raw === "number" ? raw : Number(raw);
      return {
        ...item,
        value: Number.isFinite(n) ? n : 0,
      };
    }) as PieChartDataItem[];
  }, [dataProp, cgmCumulativeLogs.data]);

  const outerRadius = 100;
  const innerRadius = 80;
  const lineLength = 20;

  useEffect(() => {
    if (!ref.current) return;

    const tooltip = d3
      .select("body")
      .append("div")
      .style("position", "absolute")
      .style("background", "#181F2B")
      .style("color", "#fff")
      .style("padding", "8px 12px")
      .style("border-radius", "6px")
      .style("font-size", "13px")
      .style("font-weight", "500")
      .style("line-height", "1.4")
      .style("pointer-events", "none")
      .style("opacity", "0")
      .style("z-index", "50");

    d3.select(ref.current).selectAll("*").remove();

    const svg = d3
      .select(ref.current)
      .attr("width", width)
      .attr("height", height);

    const group = svg
      .append("g")
      .attr("transform", `translate(${width / 2}, ${height / 2})`);

    // Outer gray border
    group
      .append("circle")
      .attr("r", outerRadius + 2)
      .attr("fill", "none")
      .attr("stroke", "#E5E7EB")
      .attr("stroke-width", 6);

    // Filter out 0% and apply min width for visibility
    const displayData = data
      .filter((d) => d.value > 0)
      .map((d) => ({
        ...d,
        displayValue: d.value <= 1 ? 1 : d.value,
      }));

    if (displayData.length === 0) {
      // Show placeholder when no data
      group
        .append("text")
        .text("No data for selected period")
        .attr("text-anchor", "middle")
        .attr("y", 5)
        .style("font-size", "14px")
        .style("fill", "#6B7280");
      return () => tooltip.remove();
    }

    const pie = d3
      .pie<PieChartDataItem & { displayValue: number }>()
      .value((d) => d.displayValue)
      .sort(null);
    const arc = d3
      .arc<d3.PieArcDatum<PieChartDataItem & { displayValue: number }>>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius);
    const arcs = pie(displayData);

    // Draw slices
    group
      .selectAll<
        SVGPathElement,
        d3.PieArcDatum<PieChartDataItem & { displayValue: number }>
      >("path")
      .data(arcs)
      .enter()
      .append("path")
      .attr("d", arc)
      .attr("fill", (d) => d.data.color)
      .on("mouseover", function (event, d) {
        tooltip.style("opacity", "1").html(
          `<div><strong>${d.data.name}</strong></div>
           <div>${parseFloat(d.data.value.toFixed(1))} %</div>
           <div style="font-size:11px;color:#9CA3AF">Range: ${
             d.data.range
           }</div>
           <div style="font-size:11px;color:#9CA3AF">Ideal: ${
             d.data.ideal
           }</div>`
        );
        d3.select(this).attr("opacity", "0.8");
      })
      .on("mousemove", function (event) {
        tooltip
          .style("left", `${(event as MouseEvent).pageX + 15}px`)
          .style("top", `${(event as MouseEvent).pageY - 28}px`);
      })
      .on("mouseout", function () {
        tooltip.style("opacity", "0");
        d3.select(this).attr("opacity", "1");
      });

    // Label positions
    interface LabelPos {
      arcData: d3.PieArcDatum<PieChartDataItem & { displayValue: number }>;
      posA: [number, number];
      posC: [number, number];
      dotX: number;
      dotY: number;
    }

    const labelArc = d3
      .arc<d3.PieArcDatum<PieChartDataItem & { displayValue: number }>>()
      .innerRadius(outerRadius)
      .outerRadius(outerRadius);
    const labelPositions: LabelPos[] = arcs.map((d) => {
      const posA = labelArc.centroid(d) as [number, number];
      const angle = Math.atan2(posA[1], posA[0]);
      const posC: [number, number] = [
        posA[0] + lineLength * Math.cos(angle),
        posA[1] + lineLength * Math.sin(angle),
      ];
      return {
        arcData: d,
        posA,
        posC,
        dotX: posC[0],
        dotY: posC[1],
      };
    });

    labelPositions.sort((a, b) => a.dotY - b.dotY);

    const minGap = 18;
    for (let i = 1; i < labelPositions.length; i++) {
      if (labelPositions[i].dotY - labelPositions[i - 1].dotY < minGap) {
        labelPositions[i].dotY = labelPositions[i - 1].dotY + minGap;
      }
    }

    labelPositions.forEach((p) => {
      p.posC[1] = p.dotY;
    });

    // Draw polylines
    group
      .selectAll("polyline")
      .data(labelPositions)
      .enter()
      .append("polyline")
      .attr("points", (p) => `${p.posA[0]},${p.posA[1]} ${p.dotX},${p.dotY}`)
      .attr("stroke", "#181F2B")
      .attr("stroke-dasharray", "4 3")
      .attr("fill", "none")
      .attr("stroke-width", 1.5);

    // Draw labels
    group
      .selectAll("text.label")
      .data(labelPositions)
      .enter()
      .append("text")
      .attr("class", "label")
      .text((p) => `${parseFloat(p.arcData.data.value.toFixed(1))} %`)
      .attr("x", (p) => p.dotX + (p.dotX >= 0 ? 6 : -6))
      .attr("y", (p) => p.dotY)
      .style("text-anchor", (p) => (p.dotX >= 0 ? "start" : "end"))
      .style("dominant-baseline", "middle")
      .style("font-size", "14px")
      .style("font-weight", "bold")
      .style("fill", "#181F2B");

    // Draw dots
    group
      .selectAll("circle.dot")
      .data(labelPositions)
      .enter()
      .append("circle")
      .attr("class", "dot")
      .attr("cx", (p) => p.dotX)
      .attr("cy", (p) => p.dotY)
      .attr("r", 3)
      .attr("fill", "#181F2B");

    // Center: radial gradient circle (no mesh image in this project)
    group
      .append("defs")
      .append("radialGradient")
      .attr("id", "pie-center-gradient")
      .selectAll("stop")
      .data([
        { offset: "0%", color: "#E5E7EB" },
        { offset: "100%", color: "#F3F4F6" },
      ])
      .enter()
      .append("stop")
      .attr("offset", (d) => d.offset)
      .attr("stop-color", (d) => d.color);

    group
      .append("circle")
      .attr("r", 60)
      .attr("fill", "url(#pie-center-gradient)");

    // Center text
    group
      .append("text")
      .text("Time In Ranges")
      .attr("text-anchor", "middle")
      .attr("y", 5)
      .style("font-size", "16px")
      .style("font-weight", "bold")
      .style("fill", "#181F2B");

    return () => {
      tooltip.remove();
    };
  }, [data, width, height]);

  return (
    <div className={classNames("flex flex-col items-center", wrapperClass)}>
      <svg ref={ref} />
      {showLegend && (
        <div
          className={classNames(
            "flex flex-wrap justify-center gap-3 mt-3 px-4",
            legendWrapperClass
          )}
        >
          {data.map((item) => (
            <div key={item.name} className="flex items-center gap-1">
              <span
                className="inline-block w-4 h-4 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-xs font-semibold text-[#181F2B]">
                {item.name}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PieChartRepresentation;
